import React from "react";
import { Music, Film, Sparkles, ArrowRight, Radio } from "lucide-react";

/**
 * Suno x Fliboe FM promo card — 1200x1200 square poster.
 * Zero LLM cost: pure HTML/CSS with a stock concert background via Unsplash CDN.
 * Route: /brochure/suno-fliboe (admin only via AdminOnlyBrochure wrapper).
 */
export default function BrochureSunoFliboe() {
  return (
    <div
      style={{
        width: 1200,
        height: 1200,
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Inter', system-ui, sans-serif",
        background: "#000",
      }}
    >
      {/* Concert background photo */}
      <img
        src="https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=1400&q=80"
        alt=""
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          objectFit: "cover", opacity: 0.85,
        }}
      />
      {/* Dark bottom gradient for readability */}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.95) 90%)",
      }} />

      {/* Two-logo header */}
      <div style={{
        position: "absolute", top: 90, left: 0, right: 0,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 26,
      }}>
        {/* Suno tile */}
        <div style={{
          width: 130, height: 130, borderRadius: 30,
          background: "linear-gradient(135deg, #22d3ee 0%, #0ea5e9 100%)",
          boxShadow: "0 20px 40px rgba(14,165,233,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 900, fontSize: 32, color: "#fff", letterSpacing: "0.02em",
        }}>
          suno
        </div>
        <div style={{ color: "#fff", fontSize: 34, fontWeight: 300, opacity: 0.9 }}>×</div>
        {/* Fliboe tile */}
        <div style={{
          width: 130, height: 130, borderRadius: 30,
          background: "linear-gradient(135deg, #10b981 0%, #065f46 100%)",
          boxShadow: "0 20px 40px rgba(245,158,11,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Radio color="#fff" size={64} strokeWidth={2.5} />
        </div>
      </div>

      {/* Headline */}
      <div style={{
        position: "absolute", top: 260, left: 0, right: 0, textAlign: "center", padding: "0 60px",
      }}>
        <div style={{
          color: "#fff", fontSize: 68, fontWeight: 900, lineHeight: 1.05,
          letterSpacing: "-0.02em",
          textShadow: "0 4px 20px rgba(0,0,0,0.6)",
        }}>
          Turn Your Suno Songs
          <br />
          Into A Worldwide Radio Station
        </div>
      </div>

      {/* 3-step cards */}
      <div style={{
        position: "absolute", top: 520, left: 60, right: 60,
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 22,
      }}>
        {[
          { n: 1, icon: <Music color="#fff" size={54} strokeWidth={2.2} />, title: "Make Your Song", desc: "Create tracks with Suno's AI in minutes" },
          { n: 2, icon: <Film color="#fff" size={54} strokeWidth={2.2} />, title: "Upload to Fliboe", desc: "Drop the MP3 into your Fliboe station" },
          { n: 3, icon: <Sparkles color="#fff" size={54} strokeWidth={2.2} />, title: "Broadcast Worldwide", desc: "AI DJ intros your track on every listener's radio" },
        ].map(s => (
          <div key={s.n} style={{
            background: "rgba(20,20,20,0.85)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 24,
            padding: "24px 22px 26px",
            backdropFilter: "blur(20px)",
          }}>
            <div style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 34, height: 34, borderRadius: 10,
              background: "rgba(255,255,255,0.12)", color: "#fff",
              fontWeight: 800, fontSize: 16, marginBottom: 14,
            }}>{s.n}</div>
            <div style={{ marginBottom: 12 }}>{s.icon}</div>
            <div style={{ color: "#fff", fontSize: 22, fontWeight: 800, marginBottom: 6 }}>{s.title}</div>
            <div style={{ color: "#cbd5e1", fontSize: 16, lineHeight: 1.4 }}>{s.desc}</div>
          </div>
        ))}
      </div>

      {/* CTA button */}
      <div style={{
        position: "absolute", bottom: 220, left: 0, right: 0, textAlign: "center",
      }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 12,
          padding: "20px 60px", borderRadius: 9999,
          background: "linear-gradient(90deg, #f472b6 0%, #10b981 100%)",
          boxShadow: "0 20px 50px rgba(245,158,11,0.45), 0 0 100px rgba(244,114,182,0.35)",
          color: "#0a0a0a", fontWeight: 800, fontSize: 26,
        }}>
          Try It Free <ArrowRight size={28} strokeWidth={3} />
        </div>
      </div>

      {/* Waveform bottom decoration */}
      <div style={{
        position: "absolute", bottom: 110, left: 40, right: 40,
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        height: 50, opacity: 0.7,
      }}>
        {Array.from({ length: 60 }).map((_, i) => {
          const h = 8 + Math.abs(Math.sin(i * 0.6) * 40) + Math.abs(Math.cos(i * 0.3) * 8);
          return (
            <div key={i} style={{
              width: 6, height: h, borderRadius: 3,
              background: `linear-gradient(180deg, rgba(245,158,11,0.9), rgba(244,114,182,0.6))`,
            }} />
          );
        })}
      </div>

      {/* Footer line */}
      <div style={{
        position: "absolute", bottom: 55, left: 0, right: 0, textAlign: "center",
        color: "#cbd5e1", fontSize: 22, fontWeight: 500,
      }}>
        Sign up on <span style={{ color: "#34d399", fontWeight: 800 }}>fliboe.com</span> · 1 Month Premium Free
      </div>
    </div>
  );
}
